import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useAddShopOwnerShipDetail } from "../../API_Master/MutateAPI";
import Cookies from "js-cookie";
import { handleVenderLogOut, notifyError, notifySuccess } from "../../HelperMethods";
import Flatpickr from "react-flatpickr";
import FormLanguageSync from "../FormLanguageSync";
import AgreementUpload from "./AgreementUpload";
import FullScreenLoader from "../FullScreenLoader";
import { useNavigate } from "react-router-dom";
import { mainRouteInitial } from "../../Data/Constant";
const OwnershipDetails = ({ nextStep, prevStep, shopDetailsData }) => {
  const { t } = useTranslation();
  const { mutate: addShopOwnerShipDetail, isPending: addingShopOwnerShipDetail } = useAddShopOwnerShipDetail();
  const navigate = useNavigate();

  const initialValues = {
    hasRentAgreement: shopDetailsData?.[0]?.IsRentAgreement || "no",
    ownershipType:
      shopDetailsData?.[0]?.IsShopOwnorOnRentOrOnSubTenant == "OnRent"
        ? "rent"
        : shopDetailsData?.[0]?.IsShopOwnorOnRentOrOnSubTenant == "Owned"
          ? "owned"
          : shopDetailsData?.[0]?.IsShopOwnorOnRentOrOnSubTenant == "OnSubTenant"
            ? "subtenant"
            : "owned",
    ownerName: shopDetailsData?.[0]?.ShopOwnerDetail?.[0]?.OwnerName || "",
    ownerMobile: shopDetailsData?.[0]?.ShopOwnerDetail?.[0]?.OwnerMobileNo || "",
    // ── Fixed & safe agreementExpiry ──
    agreementExpiry: shopDetailsData?.[0]?.RentExpiryDate ? moment(shopDetailsData?.[0]?.RentExpiryDate, "DD-MM-YYYY").toDate() : "",
    ownerAddress: shopDetailsData?.[0]?.ShopOwnerDetail?.[0]?.OwnerAddress || "",
    rentAgreementFile: "",
    oldRentAgreementExpiry: shopDetailsData?.[0]?.ShopAgreementDetail?.filter?.((ele) => ele?.AttachmentType === "ShopAgreement")?.[0]?.Attachment || "",
    mainTenantName: shopDetailsData?.[0]?.ShopOwnerDetail?.[0]?.MainTenantName || "",
    mainTenantMobileNo: shopDetailsData?.[0]?.ShopOwnerDetail?.[0]?.MainTenantMobileNo || "",
    subTenantAgreementStartDate: shopDetailsData?.[0]?.SubTenantAgreementStartDate ? moment(shopDetailsData?.[0]?.SubTenantAgreementStartDate, "DD-MM-YYYY").toDate() : "",
    subTenantAgreementExpiryDate: shopDetailsData?.[0]?.SubTenantAgreementExpiryDate ? moment(shopDetailsData?.[0]?.SubTenantAgreementExpiryDate, "DD-MM-YYYY").toDate() : "",
    mainTenantAddress: shopDetailsData?.[0]?.ShopOwnerDetail?.[0]?.MainTenantAddress || "",
    subTenantAgreementImage: "",
    oldSubTenantAgreementImage: shopDetailsData?.[0]?.ShopAgreementDetail?.filter?.((ele) => ele?.AttachmentType === "SubTenantShopAgreement")?.[0]?.Attachment || "",
    shopAgreementDetailID: shopDetailsData?.[0]?.ShopAgreementDetail?.filter?.((ele) => ele?.AttachmentType === "ShopAgreement")?.[0]?.ShopAgreementDetailID || "",
    subTenantShopAgreementDetailID:
      shopDetailsData?.[0]?.ShopAgreementDetail?.filter?.((ele) => ele?.AttachmentType === "SubTenantShopAgreement")?.[0]?.ShopAgreementDetailID || "",
    shopOwnerDetailID: shopDetailsData?.[0]?.ShopOwnerDetail?.[0]?.ShopOwnerDetailID || "",
  };

  const baseSchema = Yup.object({
    ownershipType: Yup.string().oneOf(["owned", "rent", "subtenant"], t("invalidOwnershipType")),

    hasRentAgreement: Yup.boolean()
      // only relevant for rent & subtenant
      .when("ownershipType", {
        is: (v) => ["rent", "subtenant"].includes(v),
        then: (s) => s.required("Please tell us whether you have an agreement"),
        otherwise: (s) => s.nullable().default(false),
      }),
  });

  const validationSchema = Yup.object({
    ownershipType: Yup.string()
      .oneOf(["owned", "rent", "subtenant"], t("invalidOwnershipType"))
      .required(t("ownershipStatus") + " " + t("isRequired")),

    // hasRentAgreement: Yup.boolean().when("ownershipType", {
    //   is: (v) => ["rent", "subtenant"].includes(v),
    //   then: (s) => s.required(t("Please tell us whether you have an agreement")),
    //   otherwise: (s) => s.nullable().default(false),
    // }),

    hasRentAgreement: Yup.string().when("ownershipType", {
      is: (v) => ["rent", "subtenant"].includes(v),
      then: (s) => s.required(t("Please tell us whether you have an agreement")),
      otherwise: (s) => s.nullable().default(false),
    }),

    // ── Common rent/subtenant fields ───────────────────────────────
    ownerName: Yup.string().when(["ownershipType", "hasRentAgreement"], {
      is: (ownershipType, hasRentAgreement) => ["rent", "subtenant"].includes(ownershipType) && hasRentAgreement === "yes",
      then: (s) => s.required(t("ownerFullName") + " " + t("isRequired")).min(3, t("minimumCharacters")),
      otherwise: (s) => s.nullable().notRequired(),
    }),

    ownerMobile: Yup.string().when(["ownershipType", "hasRentAgreement"], {
      is: (ownershipType, hasRentAgreement) => ["rent", "subtenant"].includes(ownershipType) && hasRentAgreement === "yes",
      then: (s) => s.required(t("ownerMobileNumber") + " " + t("isRequired")).matches(/^[1-9]\d{9}$/, t("invalidMobile")),
      otherwise: (s) => s.nullable().notRequired(),
    }),

    agreementExpiry: Yup.date().when(["ownershipType", "hasRentAgreement"], {
      is: (ownershipType, hasRentAgreement) => ["rent", "subtenant"].includes(ownershipType) && hasRentAgreement === "yes",
      then: (s) =>
        s
          .required(t("rentAgreementExpiry") + " " + t("isRequired"))
          .test("is-future", t("expiryDateMustBeInFuture"), (value) => !value || moment(value).isAfter(moment(), "day"))
          .typeError(t("pleaseSelectValidDate")),
      otherwise: (s) => s.nullable().notRequired(),
    }),

    ownerAddress: Yup.string().when(["ownershipType", "hasRentAgreement"], {
      is: (ownershipType, hasRentAgreement) => ["rent", "subtenant"].includes(ownershipType) && hasRentAgreement === "yes",
      then: (s) => s.required(t("ownerResidentialAddress") + " " + t("isRequired")).min(15, t("addressTooShort")),
      otherwise: (s) => s.nullable().notRequired(),
    }),

    // ── Subtenant-only fields ──────────────────────────────────────
    mainTenantName: Yup.string().when(["ownershipType", "hasRentAgreement"], {
      is: (ownershipType, hasRentAgreement) => ownershipType === "subtenant" && hasRentAgreement === "yes",
      then: (s) => s.required(t("mainTenantName") + " " + t("isRequired")).min(3, t("minimumCharacters")),
      otherwise: (s) => s.nullable().notRequired(),
    }),

    mainTenantMobileNo: Yup.string().when(["ownershipType", "hasRentAgreement"], {
      is: (ownershipType, hasRentAgreement) => ownershipType === "subtenant" && hasRentAgreement === "yes",
      then: (s) => s.required(t("mainTenantMobileNo") + " " + t("isRequired")).matches(/^[1-9]\d{9}$/, t("invalidMobile")),
      otherwise: (s) => s.nullable().notRequired(),
    }),

    mainTenantAddress: Yup.string().when(["ownershipType", "hasRentAgreement"], {
      is: (ownershipType, hasRentAgreement) => ownershipType === "subtenant" && hasRentAgreement === "yes",
      then: (s) => s.required(t("mainTenantAddress") + " " + t("isRequired")).min(15, t("addressTooShort")),
      otherwise: (s) => s.nullable().notRequired(),
    }),

    subTenantAgreementStartDate: Yup.date().when(["ownershipType", "hasRentAgreement"], {
      is: (ownershipType, hasRentAgreement) => ownershipType === "subtenant" && hasRentAgreement === "yes",
      then: (s) =>
        s
          .required(t("agreementStartDate") + " " + t("isRequired"))
          .test("is-future-or-today", t("startDateMustBeInFuture"), (value) => !value || moment(value).isSameOrAfter(moment(), "day"))
          .test("before-or-same-expiry", t("startDateBeforeExpiry"), function (value) {
            const { subTenantAgreementExpiryDate } = this.parent;
            return !value || !subTenantAgreementExpiryDate || moment(value).isSameOrBefore(subTenantAgreementExpiryDate, "day");
          })
          .typeError(t("pleaseSelectValidDate")),
      otherwise: (s) => s.nullable().notRequired(),
    }),

    subTenantAgreementExpiryDate: Yup.date().when(["ownershipType", "hasRentAgreement"], {
      is: (ownershipType, hasRentAgreement) => ownershipType === "subtenant" && hasRentAgreement === "yes",
      then: (s) =>
        s
          .required(t("rentAgreementExpiry") + " " + t("isRequired"))
          .test("is-future", t("expiryDateMustBeInFuture"), (value) => !value || moment(value).isAfter(moment(), "day"))
          .test("after-or-same-start", t("expiryAfterStartDate"), function (value) {
            const { subTenantAgreementStartDate } = this.parent;
            return !value || !subTenantAgreementStartDate || moment(value).isSameOrAfter(subTenantAgreementStartDate, "day");
          })
          .typeError(t("pleaseSelectValidDate")),
      otherwise: (s) => s.nullable().notRequired(),
    }),

    // ── File uploads ───────────────────────────────────────────────
    rentAgreementFile: Yup.mixed().when(["ownershipType", "hasRentAgreement", "oldRentAgreementExpiry"], {
      is: (ownershipType, hasRentAgreement, oldExpiry) => ownershipType === "rent" && hasRentAgreement === "yes" && !oldExpiry,
      then: (s) =>
        s
          .required(t("uploadRentAgreement") + " " + t("isRequired"))
          .test("fileSize", t("fileTooLarge"), (v) => !v || v.size <= 5000000)
          .test("fileType", t("invalidFileType"), (v) => !v || ["application/pdf", "image/jpeg", "image/jpg", "image/png"].includes(v.type)),
      otherwise: (s) =>
        s
          .nullable()
          .test("fileSize", t("fileTooLarge"), (v) => !v || v.size <= 5000000)
          .test("fileType", t("invalidFileType"), (v) => !v || ["application/pdf", "image/jpeg", "image/jpg", "image/png"].includes(v.type)),
    }),

    subTenantAgreementImage: Yup.mixed().when(["ownershipType", "hasRentAgreement", "oldSubTenantAgreementImage"], {
      is: (ownershipType, hasRentAgreement, oldImage) => ownershipType === "subtenant" && hasRentAgreement === "yes" && !oldImage,
      then: (s) =>
        s
          .required(t("uploadSubTenantAgreement") + " " + t("isRequired"))
          .test("fileSize", t("fileTooLarge"), (v) => !v || v.size <= 5000000)
          .test("fileType", t("invalidFileType"), (v) => !v || ["application/pdf", "image/jpeg", "image/jpg", "image/png"].includes(v.type)),
      otherwise: (s) =>
        s
          .nullable()
          .test("fileSize", t("fileTooLarge"), (v) => !v || v.size <= 5000000)
          .test("fileType", t("invalidFileType"), (v) => !v || ["application/pdf", "image/jpeg", "image/jpg", "image/png"].includes(v.type)),
    }),
  });

  const handleAllStepCompleted = (message) => {
    notifySuccess(message);
    navigate(`/${mainRouteInitial}/registerVendorList`);
  };
  const inputBaseClasses = "w-full h-12 px-4 rounded-lg bg-slate-50 dark:bg-slate-800 text-[#0d141b] dark:text-white placeholder:text-slate-400 transition-all outline-none";
  const inputFocusClasses = "focus:ring-2 focus:ring-primary/20 focus:border-primary";
  const inputErrorClasses = "border-red-500";
  const inputNormalBorder = "border border-slate-300 dark:border-slate-600";
  const textareaBaseClasses =
    "w-full py-3 px-4 rounded-lg bg-slate-50 dark:bg-slate-800 text-[#0d141b] dark:text-white placeholder:text-slate-400 transition-all outline-none resize-y";

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        //  enableReinitialize={true}
        // enableReinitialize={true}
        onSubmit={(values) => {
          let showOnRentData = {};
          if (values?.ownershipType == "rent") {
            showOnRentData = {
              ShopOwnerDetailID: values?.shopOwnerDetailID || "",
              OwnerName: values?.ownerName || "",
              OwnerMobileNo: values?.ownerMobile || "",
              OwnerAddress: values?.ownerAddress || "",
              RentExpiryDate: values.agreementExpiry ? moment(values?.agreementExpiry)?.format("DD-MM-YYYY") : "",
              CreatedByUserID: Cookies.get("NIGHT_EYE_USER_ID") || "",
              ShopAgreementImage: values.rentAgreementFile || "",
              OldShopAgreementImage: values?.oldRentAgreementExpiry || "",
              ShopAgreementDetailID: values?.shopAgreementDetailID || "",
              // IsRentAgreement: values?.hasRentAgreement ? "1" : "0",
              IsRentAgreement: values?.hasRentAgreement,
              OldSubTenantShopAgreementDetailID: values?.subTenantShopAgreementDetailID || "",
            };
          } else if (values?.ownershipType == "subtenant") {
            showOnRentData = {
              ShopOwnerDetailID: values?.shopOwnerDetailID || "",
              OwnerName: values?.ownerName || "",
              OwnerMobileNo: values?.ownerMobile || "",
              OwnerAddress: values?.ownerAddress || "",
              RentExpiryDate: values.agreementExpiry ? moment(values.agreementExpiry).format("DD-MM-YYYY") : "",
              ShopAgreementImage: values.rentAgreementFile || "",
              OldShopAgreementImage: values?.oldRentAgreementExpiry || "",
              MainTenantName: values?.mainTenantName || "",
              MainTenantMobileNo: values?.mainTenantMobileNo || "",
              MainTenantAddress: values?.mainTenantAddress || "",
              SubTenantAgreementStartDate: values.subTenantAgreementStartDate ? moment(values.subTenantAgreementStartDate).format("DD-MM-YYYY") : "",
              SubTenantAgreementExpiryDate: values.subTenantAgreementExpiryDate ? moment(values.subTenantAgreementExpiryDate).format("DD-MM-YYYY") : "",
              CreatedByUserID: Cookies.get("NIGHT_EYE_USER_ID") || "",
              ShopAgreementDetailID: values?.shopAgreementDetailID || "",
              SubTenantShopAgreementDetailID: values?.subTenantShopAgreementDetailID || "",
              SubTenantAgreementImage: values.subTenantAgreementImage || "",
              OldSubTenantAgreementImage: values?.oldSubTenantAgreementImage || "",
              // IsRentAgreement: values?.hasRentAgreement ? "1" : "0",
              IsRentAgreement: values?.hasRentAgreement,
            };
          } else {
            showOnRentData = {
              OldShopAgreementDetailID: values?.shopAgreementDetailID || "",
              OldSubTenantShopAgreementDetailID: values?.subTenantShopAgreementDetailID || "",
              CreatedByUserID: Cookies.get("NIGHT_EYE_USER_ID") || "",
            };
          }

          const payload = {
            UserID: Cookies.get("VENDER_NIGHT_EYE_USER_ID") || "",
            ShopDetailID: Cookies.get("NIGHT_EYE_SHOP_DETAIL_ID") || "",
            IsShopOwnorOnRentOrOnSubTenant:
              values?.ownershipType == "rent" ? "OnRent" : values?.ownershipType == "owned" ? "Owned" : values?.ownershipType == "subtenant" ? "OnSubTenant" : "",
            ...showOnRentData,
          };
          addShopOwnerShipDetail(payload, {
            onSuccess: (res) => {
              if (res?.status === true) {
                Cookies.set("IsStep3Completed", true);
                // nextStep();
                handleAllStepCompleted(res?.message || "Vendor Registration Successfull");
              } else {
                notifyError(res?.message || "Data Not Inserted");
              }
            },
            onError: (err) => {
              notifyError("Failed to Insert User");
            },
          });
        }}
      >
        {({ values, touched, errors, setFieldValue, handleBlur }) => (
          <>
            <FormLanguageSync />
            <Form className="bg-white dark:bg-[#1A2633] rounded-xl shadow-sm border border-[#e7edf3] dark:border-slate-700 overflow-hidden">
              {/* Ownership Status */}
              <div className="p-6 md:p-8 border-b border-[#e7edf3] dark:border-slate-700">
                <label className="block text-xs font-bold font-semibold text-[#0d141b] dark:text-white mb-4">{t("ownershipStatus")}</label>
                <div className="grid md:grid-cols-2 gap-4">
                  <label
                    className={`group relative flex items-start gap-4 rounded-xl border-2 p-4 cursor-pointer transition-all ${values.ownershipType === "owned" ? "border-primary bg-primary/5 dark:bg-primary/10" : "border-transparent bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-750"}`}
                  >
                    <input
                      className="mt-1 h-5 w-5 border-slate-300 text-primary focus:ring-primary"
                      name="ownershipType"
                      type="radio"
                      value="owned"
                      onChange={() => setFieldValue("ownershipType", "owned")}
                      checked={values.ownershipType == "owned"}
                    />
                    <div className="flex flex-col">
                      <span className="text-[#0d141b] dark:text-white font-bold text-base mb-1">{t("shopOwned")}</span>
                      <span className="text-[#4c739a] dark:text-slate-400 text-xs font-bold">{t("shopOwnedDesc")}</span>
                    </div>
                    {values.ownershipType === "owned" && (
                      <div className="absolute top-4 right-4 text-primary">
                        <span className="material-symbols-outlined">check_circle</span>
                      </div>
                    )}
                  </label>

                  <label
                    className={`group relative flex items-start gap-4 rounded-xl border-2 p-4 cursor-pointer transition-all ${values.ownershipType === "rent" ? "border-primary bg-primary/5 dark:bg-primary/10" : "border-transparent bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-750"}`}
                  >
                    <input
                      className="mt-1 h-5 w-5 border-slate-300 text-primary focus:ring-primary"
                      name="ownershipType"
                      type="radio"
                      value="rent"
                      onChange={() => setFieldValue("ownershipType", "rent")}
                      checked={values.ownershipType == "rent"}
                    />
                    <div className="flex flex-col">
                      <span className="text-[#0d141b] dark:text-white font-bold text-base mb-1">{t("shopOnRent")}</span>
                      <span className="text-[#4c739a] dark:text-slate-400 text-xs font-bold">{t("shopOnRentDesc")}</span>
                    </div>
                    {values.ownershipType === "rent" && (
                      <div className="absolute top-4 right-4 text-primary">
                        <span className="material-symbols-outlined">check_circle</span>
                      </div>
                    )}
                  </label>
                  <label
                    className={`group relative flex items-start gap-4 rounded-xl border-2 p-4 cursor-pointer transition-all ${values.ownershipType === "subtenant" ? "border-primary bg-primary/5 dark:bg-primary/10" : "border-transparent bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-750"}`}
                  >
                    <input
                      className="mt-1 h-5 w-5 border-slate-300 text-primary focus:ring-primary"
                      name="ownershipType"
                      type="radio"
                      value="subtenant"
                      onChange={() => setFieldValue("ownershipType", "subtenant")}
                      checked={values.ownershipType === "subtenant"}
                    />
                    <div className="flex flex-col">
                      <span className="text-[#0d141b] dark:text-white font-bold text-base mb-1">{t("shopSubTenant")}</span>
                      <span className="text-[#4c739a] dark:text-slate-400 text-xs font-bold">{t("shopSubTenantDesc")}</span>
                    </div>
                    {values.ownershipType === "subtenant" && (
                      <div className="absolute top-4 right-4 text-primary">
                        <span className="material-symbols-outlined">check_circle</span>
                      </div>
                    )}
                  </label>
                </div>
                {touched.ownershipType && errors.ownershipType && <p className="text-red-800 text-xs font-bold ">{errors.ownershipType}</p>}
              </div>
              {["rent", "subtenant"].includes(values.ownershipType) && (
                <div className="p-6 md:p-8 border-t border-[#e7edf3] dark:border-slate-700">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                    {values.ownershipType === "rent" ? t("doYouHaveRentAgreement") : t("doYouHaveSubTenantAgreement")}
                    <span className="text-red-600 ml-1">*</span>
                  </label>

                  {/* <div className="flex items-center gap-8">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="hasRentAgreement"
                        checked={values.hasRentAgreement === true}
                        disabled={shopDetailsData?.[0]?.IsRentAgreement }
                        onChange={() => setFieldValue("hasRentAgreement", true)}
                        className="h-5 w-5 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-gray-700 dark:text-gray-200 font-medium">{t("yes")}</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="hasRentAgreement"
                        checked={values.hasRentAgreement === false}
                        disabled={shopDetailsData?.[0]?.IsRentAgreement }
                        onChange={() => setFieldValue("hasRentAgreement", false)}
                        className="h-5 w-5 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-gray-700 dark:text-gray-200 font-medium">{t("no")}</span>
                    </label>
                  </div> */}

                  <div className="flex items-center gap-8">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="hasRentAgreement"
                        checked={values.hasRentAgreement === "yes"}
                        disabled={shopDetailsData?.[0]?.IsRentAgreement}
                        onChange={() => setFieldValue("hasRentAgreement", "yes")}
                        className="h-5 w-5 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-gray-700 dark:text-gray-200 font-medium">{t("yes")}</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="hasRentAgreement"
                        checked={values.hasRentAgreement === "no"}
                        disabled={shopDetailsData?.[0]?.IsRentAgreement}
                        onChange={() => setFieldValue("hasRentAgreement", "no")}
                        className="h-5 w-5 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-gray-700 dark:text-gray-200 font-medium">{t("no")}</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="hasRentAgreement"
                        checked={values.hasRentAgreement === "notapplicable"}
                        disabled={shopDetailsData?.[0]?.IsRentAgreement}
                        onChange={() => setFieldValue("hasRentAgreement", "notapplicable")}
                        className="h-5 w-5 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-gray-700 dark:text-gray-200 font-medium">{t("Not Applicable")}</span>
                    </label>
                  </div>

                  {touched.ownershipType && errors.hasRentAgreement && <p className="mt-2 text-sm text-red-600">{errors.hasRentAgreement}</p>}
                </div>
              )}
              {/* Property Owner Details */}
              {(values.ownershipType === "rent" || values.ownershipType === "subtenant") && (
                <div className="p-6 md:p-8 space-y-8">
                  <div className="flex items-center gap-2 pb-2 border-b border-slate-100 dark:border-slate-700">
                    <span className="material-symbols-outlined text-primary">person_pin</span>
                    <h3 className="text-[#0d141b] dark:text-white text-lg font-bold">{t("propertyOwnerDetails")}</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-bold font-medium text-[#0d141b] dark:text-slate-200">
                        {t("ownerFullName")} <span className="text-red-800">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined text-[20px]">badge</span>
                        <input
                          name="ownerName"
                          value={values.ownerName}
                          onChange={(e) => setFieldValue("ownerName", e.target.value)}
                          onBlur={handleBlur}
                          className={`${inputBaseClasses} pl-10 ${inputFocusClasses} ${touched.ownerName && errors.ownerName ? inputErrorClasses : inputNormalBorder}`}
                          placeholder={t("ownerFullName")}
                          type="text"
                        />
                      </div>
                      {touched.ownerName && errors.ownerName && <p className="text-red-800 text-xs font-bold mt-1">{errors.ownerName}</p>}
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold font-medium text-[#0d141b] dark:text-slate-200">
                        {t("ownerMobileNumber")} <span className="text-red-800">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined text-[20px]">phone_iphone</span>
                        <input
                          name="ownerMobile"
                          value={values.ownerMobile}
                          onChange={(e) => setFieldValue("ownerMobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
                          onBlur={handleBlur}
                          className={`${inputBaseClasses} pl-10 ${inputFocusClasses} ${touched.ownerMobile && errors.ownerMobile ? inputErrorClasses : inputNormalBorder}`}
                          placeholder="+91 98765 43210"
                          type="tel"
                        />
                      </div>
                      {touched.ownerMobile && errors.ownerMobile && <p className="text-red-800 text-xs font-bold mt-1">{errors.ownerMobile}</p>}
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold font-medium text-[#0d141b] dark:text-slate-200">
                        {t("rentAgreementExpiry")} <span className="text-red-800">*</span>
                      </label>

                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined text-[20px]">calendar_month</span>

                        <Flatpickr
                          name="agreementExpiry"
                          value={values.agreementExpiry} // Accepts Date object
                          onChange={([date]) => {
                            // Store as Date object (best for Yup date validation)
                            setFieldValue("agreementExpiry", date || null);
                          }}
                          options={{
                            dateFormat: "d-m-Y",
                            minDate: "today", // No past dates in UI
                            defaultDate: values.agreementExpiry || moment().toDate(),
                            altInput: true,
                            altFormat: "d-m-Y",
                            placeholder: "DD-MM-YYYY",
                            allowInput: true,
                            onClose: (selectedDates) => {
                              // Optional: extra safety when user types manually
                              if (selectedDates.length > 0) {
                                setFieldValue("agreementExpiry", selectedDates[0]);
                              }
                            },
                          }}
                          className={`
                           w-full pl-10 pr-4 py-2 border rounded-lg
                          ${inputBaseClasses} ${inputFocusClasses}
                          ${touched.agreementExpiry && errors.agreementExpiry ? "border-red-500 focus:border-red-500 ring-red-200" : "border-gray-300 focus:border-blue-500"}
                           `}
                        />
                      </div>

                      {touched.agreementExpiry && errors.agreementExpiry && <p className="text-red-800 text-xs font-bold mt-1">{errors.agreementExpiry}</p>}
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs font-bold font-medium text-[#0d141b] dark:text-slate-200">
                        {t("ownerResidentialAddress")} <span className="text-red-800">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-3 text-slate-400 material-symbols-outlined text-[20px]">home_pin</span>
                        <textarea
                          name="ownerAddress"
                          value={values.ownerAddress}
                          onChange={(e) => setFieldValue("ownerAddress", e.target.value)}
                          onBlur={handleBlur}
                          className={`${textareaBaseClasses} pl-10 ${inputFocusClasses} ${touched.ownerAddress && errors.ownerAddress ? inputErrorClasses : inputNormalBorder}`}
                          placeholder={t("ownerResidentialAddress")}
                          rows={3}
                        />
                      </div>
                      {touched.ownerAddress && errors.ownerAddress && <p className="text-red-800 text-xs font-bold mt-1">{errors.ownerAddress}</p>}
                    </div>
                  </div>

                  {/* Documents Upload */}
                  <div className="space-y-4">
                    <AgreementUpload fieldName="rentAgreementFile" oldFieldName="oldRentAgreementExpiry" titleKey="uploadRentAgreement" />
                  </div>
                </div>
              )}
              {values.ownershipType === "subtenant" && (
                <div className="p-6 md:p-8 space-y-8">
                  <div className="flex items-center gap-2 pb-2 border-b border-slate-100 dark:border-slate-700">
                    <span className="material-symbols-outlined text-primary">person_pin</span>
                    <h3 className="text-[#0d141b] dark:text-white text-lg font-bold">{t("mainTenantDetails")}</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-[#0d141b] dark:text-slate-200">
                        {t("mainTenantName")} <span className="text-red-800">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined text-[20px]">badge</span>
                        <input
                          value={values.mainTenantName}
                          onChange={(e) => setFieldValue("mainTenantName", e.target.value)}
                          onBlur={handleBlur}
                          className={`${inputBaseClasses} pl-10 ${inputFocusClasses} ${touched.mainTenantName && errors.mainTenantName ? inputErrorClasses : inputNormalBorder}`}
                          placeholder={t("mainTenantName")}
                        />
                      </div>
                      {touched.mainTenantName && errors.mainTenantName && <p className="text-red-800 text-xs font-bold mt-1">{errors.mainTenantName}</p>}
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold text-[#0d141b] dark:text-slate-200">
                        {t("mainTenantMobileNo")} <span className="text-red-800">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined text-[20px]">phone_iphone</span>
                        <input
                          value={values.mainTenantMobileNo}
                          onChange={(e) => setFieldValue("mainTenantMobileNo", e.target.value.replace(/\D/g, "").slice(0, 10))}
                          onBlur={handleBlur}
                          className={`${inputBaseClasses} pl-10 ${inputFocusClasses} ${touched.mainTenantMobileNo && errors.mainTenantMobileNo ? inputErrorClasses : inputNormalBorder}`}
                          placeholder="+91 98765 43210"
                          type="tel"
                        />
                      </div>
                      {touched.mainTenantMobileNo && errors.mainTenantMobileNo && <p className="text-red-800 text-xs font-bold mt-1">{errors.mainTenantMobileNo}</p>}
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold text-[#0d141b] dark:text-slate-200">
                        {t("agreementStartDate")} <span className="text-red-800">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined text-[20px]">calendar_month</span>
                        <Flatpickr
                          value={values.subTenantAgreementStartDate}
                          onChange={([date]) => setFieldValue("subTenantAgreementStartDate", date || null)}
                          options={{
                            dateFormat: "d-m-Y",
                            minDate: "today",
                            altInput: true,
                            altFormat: "d-m-Y",
                            placeholder: "DD-MM-YYYY",
                            allowInput: true,
                          }}
                          className={`${inputBaseClasses} pl-10 ${inputFocusClasses} ${touched.subTenantAgreementStartDate && errors.subTenantAgreementStartDate ? inputErrorClasses : inputNormalBorder}`}
                        />
                      </div>
                      {touched.subTenantAgreementStartDate && errors.subTenantAgreementStartDate && (
                        <p className="text-red-800 text-xs font-bold mt-1">{errors.subTenantAgreementStartDate}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold text-[#0d141b] dark:text-slate-200">
                        {t("agreementExpiryDate")} <span className="text-red-800">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined text-[20px]">calendar_month</span>
                        <Flatpickr
                          value={values.subTenantAgreementExpiryDate}
                          onChange={([date]) => setFieldValue("subTenantAgreementExpiryDate", date || null)}
                          options={{
                            dateFormat: "d-m-Y",
                            minDate: "today",
                            altInput: true,
                            altFormat: "d-m-Y",
                            placeholder: "DD-MM-YYYY",
                            allowInput: true,
                          }}
                          className={`${inputBaseClasses} pl-10 ${inputFocusClasses} ${touched.subTenantAgreementExpiryDate && errors.subTenantAgreementExpiryDate ? inputErrorClasses : inputNormalBorder}`}
                        />
                      </div>
                      {touched.subTenantAgreementExpiryDate && errors.subTenantAgreementExpiryDate && (
                        <p className="text-red-800 text-xs font-bold mt-1">{errors.subTenantAgreementExpiryDate}</p>
                      )}
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs font-bold text-[#0d141b] dark:text-slate-200">
                        {t("mainTenantAddress")} <span className="text-red-800">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-3 text-slate-400 material-symbols-outlined text-[20px]">home_pin</span>
                        <textarea
                          value={values.mainTenantAddress}
                          onChange={(e) => setFieldValue("mainTenantAddress", e.target.value)}
                          onBlur={handleBlur}
                          className={`${textareaBaseClasses} pl-10 ${inputFocusClasses} ${touched.mainTenantAddress && errors.mainTenantAddress ? inputErrorClasses : inputNormalBorder}`}
                          placeholder={t("mainTenantAddress")}
                          rows={3}
                        />
                      </div>
                      {touched.mainTenantAddress && errors.mainTenantAddress && <p className="text-red-800 text-xs font-bold mt-1">{errors.mainTenantAddress}</p>}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <AgreementUpload fieldName="subTenantAgreementImage" oldFieldName="oldSubTenantAgreementImage" titleKey="uploadSubTenantAgreement" />
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="p-6 md:p-8 bg-slate-50 dark:bg-[#151f2b] border-t border-[#e7edf3] dark:border-slate-700 flex flex-col-reverse md:flex-row justify-between items-center gap-4">
                <button
                  type="button"
                  onClick={prevStep}
                  className="w-full md:w-auto px-6 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  {t("back")}
                </button>
                <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
                  <button
                    type="submit"
                    className="w-full md:w-auto px-8 py-2.5 rounded-lg bg-primary hover:bg-primary-hover text-white font-bold shadow-sm shadow-blue-500/30 transition-all flex items-center justify-center gap-2"
                  >
                    {t("saveAndContinue")}
                    <span className="material-symbols-outlined text-xs font-bold">arrow_forward</span>
                  </button>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik>
      {addingShopOwnerShipDetail && <FullScreenLoader />}
    </>
  );
};

export default OwnershipDetails;
